import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MdErrorOutline } from 'react-icons/md';
import { Button, PromoBanner, Spinner, PROMO_BANNER_TONE_ERROR } from '@aibuild/ui';
import { requestAppLinkAccess } from '@api/apps';
import { appKeys } from '../useApps';
import { PageBody, PageRoot } from '../AppRunPage.styled';

/**
 * A signed-in person opening a public link: the link becomes access to the
 * app, which from then on is listed and runs like one shared with them.
 */
const AppLinkLanding: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token = '' } = useParams<{ token: string }>();
  // A query rather than a mutation so a remount (or StrictMode) asks once; the server is idempotent anyway.
  const access = useQuery({
    queryKey: ['apps', 'linkAccess', token],
    queryFn: () => requestAppLinkAccess(token),
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  const appKey = access.data?.appKey;
  useEffect(() => {
    if (!appKey) return;
    // Dropped rather than invalidated: a cached gallery without this app would read as "not available".
    queryClient.removeQueries({ queryKey: appKeys.catalog });
    navigate(`/apps/${encodeURIComponent(appKey)}`, { replace: true });
  }, [appKey, navigate, queryClient]);

  return (
    <PageRoot>
      <PageBody>
        {access.isError ? (
          <PromoBanner
            tone={PROMO_BANNER_TONE_ERROR}
            icon={<MdErrorOutline />}
            title={intl.formatMessage({
              id: 'apps.link.dead',
              defaultMessage: 'This link no longer works. Ask the person who shared it for a new one.',
            })}
            actions={
              <Button variant="text" size="sm" onClick={() => navigate('/apps')}>
                {intl.formatMessage({ id: 'apps.builder.back', defaultMessage: 'Back to apps' })}
              </Button>
            }
            dataTestId="app-link__dead"
          />
        ) : (
          <Spinner label={intl.formatMessage({ id: 'apps.public.loading', defaultMessage: 'Opening this app…' })} />
        )}
      </PageBody>
    </PageRoot>
  );
};

export default AppLinkLanding;
